import React from 'react';
import { PortalContext } from '../../../../context/portal-context';
import { formatOwnership } from '../../../../tracking/tracking-helper';
import { useTrackingManager } from '../../../../tracking/use-tracking-manager';
import { isVehicle } from '../../../../helpers/common-helpers';
import { BenefitsCombined } from '../../benefits';
import { NormalizedVoucher, Vehicle } from '@esportal/portal-service';

// para promotions se usa el name de la promotion y para vouchers el titulo
const getBenefitTitle = (benefit: BenefitsCombined) =>
	benefit.type === 'promotion' ? benefit.data.name : benefit.data.card.title;

// contentId estándar "Benefits - <Promotion|Voucher Data> - [label -] <title>"
const buildBenefitContentId = (benefit: BenefitsCombined, label?: string) => {
	const typeLabel = benefit.type === 'promotion' ? 'Promotion' : 'Voucher Data';
	const parts = ['Benefits', typeLabel, getBenefitTitle(benefit), label].filter(Boolean);
	return parts.join(' - ');
};

export const useBenefitsTracker = () => {
	const trackingManager = useTrackingManager();

	const { portalService } = React.useContext(PortalContext);

	//Funcion interna
	const getVoucherVehicles = React.useCallback(
		(vin?: string) => {
			if (!vin) return undefined;
			const vehicle = portalService.getVehicleOrOrderByVin(vin).getValue();
			return isVehicle(vehicle) ? [vehicle] : undefined;
		},
		[portalService],
	);

	const getOwnership = (vehicles?: Vehicle[] | undefined) => {
		const hasOwnership = vehicles !== undefined;
		return hasOwnership ? { vehicle: formatOwnership(vehicles) ?? [] } : undefined;
	};

	//Funcion interna
	const trackButtonClick = React.useCallback(
		(contentIdValue: string, vehicles?: Vehicle[] | undefined) => {
			trackingManager.trackClubVWButtonClick(contentIdValue, { contentId: contentIdValue }, getOwnership(vehicles));
		},
		[trackingManager],
	);

	//Tracking para voucher salesforce, vwdata sin layer, y promotions sin layer
	const trackBenefitClick = (benefit: BenefitsCombined | undefined) => {
		if (!benefit) return;

		if (benefit.type === 'promotion') {
			trackButtonClick(buildBenefitContentId(benefit));
			return;
		}

		const contentIdValue =
			benefit.data.source === 'salesforce'
				? `Benefits - Voucher Salesforce - Campaign: ${benefit.data.campaign}`
				: `Benefits - Voucher Data - ${benefit.data.card.title}`;

		trackButtonClick(contentIdValue, getVoucherVehicles(benefit.data.vin));
	};

	//Tracking solo para voucher vw data y promociones
	const trackBenefitOpenLayerClick = (benefit: BenefitsCombined | undefined) => {
		if (!benefit) return;

		const vehicle = benefit.type === 'voucher' ? getVoucherVehicles(benefit.data.vin) : undefined;

		trackButtonClick(buildBenefitContentId(benefit, 'Open Layer'), vehicle);
	};

	//Tracking de carga de capa (layer) para voucher y promotions ya participadas
	const trackBenefitsLayerLoad = React.useCallback(
		(benefit: BenefitsCombined | undefined) => {
			if (!benefit) return;

			const vehicles = benefit.type === 'voucher' ? getVoucherVehicles(benefit.data.vin) : undefined;

			trackingManager.trackClubVWLayerLoad({ contentId: buildBenefitContentId(benefit) }, getOwnership(vehicles));
		},
		[trackingManager, getVoucherVehicles],
	);

	//Tracking de cierre de capa (layer) para voucher layer y promotions
	const trackBenefitsLayerCloseClick = (benefit: BenefitsCombined | undefined) => {
		if (!benefit) return;

		const contentIdValue = buildBenefitContentId(benefit);

		trackingManager.trackClubVWLayerCloseClick(contentIdValue, { contentId: contentIdValue });
	};

	const trackVoucherinLayerClick = (normalizedVoucher: NormalizedVoucher) => {
		const vehicles = getVoucherVehicles(normalizedVoucher.vin);

		trackButtonClick(
			`Benefits - Voucher Data - ${normalizedVoucher.card.title} - ${normalizedVoucher.layer?.cta.label}`,
			vehicles,
		);
	};

	//Tracking de carga de formulario solo para promotions
	const trackPromotionFormLayerLoad = React.useCallback(
		(formName: string) => {
			trackingManager.trackClubVWFormLoad(
				{
					FormName: formName,
					FormStart: true,
					FormTypeMultiple: ['registration'],
				},
				{ contentId: formName },
			);
		},
		[trackingManager],
	);

	//Tracking de envío de form solo para promotions
	const trackPromotionSubmissionForm = React.useCallback(
		(formName: string) => {
			trackingManager.trackClubVWFormSubmissionSuccessMessageLoad(
				{
					FormName: formName,
					FormStart: false,
					FormTypeMultiple: ['registration'],
				},
				{ contentId: formName },
			);
		},
		[trackingManager],
	);

	//Tracking de carga del sub-layer de aviso de Volkswagen Bank
	const trackVwBankLayerLoad = (normalizedVoucher: NormalizedVoucher) => {
		const contentIdValue = `Benefits - Voucher Data - ${normalizedVoucher.card.title} - Warning exit`;
		const vehicles = getVoucherVehicles(normalizedVoucher.vin);

		trackingManager.trackClubVWLayerLoad({ contentId: contentIdValue }, getOwnership(vehicles));
	};

	//Tracking de click en la CTA del sub-layer de aviso de Volkswagen Bank
	const trackVwBankButtonClick = (normalizedVoucher: NormalizedVoucher, ctaLabel?: string) => {
		const contentIdValue = `Benefits - Voucher Data - ${normalizedVoucher.card.title} - ${ctaLabel} - Warning exit`;

		trackButtonClick(contentIdValue, getVoucherVehicles(normalizedVoucher.vin));
	};

	// SALESFORCE VOUCHER DETAIL PAGE
	// Tracking de click en el botón de volver a la vista general
	const trackSalesforceVoucherDetailBackClick = (voucher: NormalizedVoucher) => {
		const contentIdValue = `Volver a la vista general - Voucher Detail - Campaign: ${voucher?.campaign}`;

		trackButtonClick(contentIdValue, getVoucherVehicles(voucher.vin));
	};

	return {
		trackBenefitsLayerLoad,
		trackBenefitsLayerCloseClick,
		trackBenefitClick,
		trackBenefitOpenLayerClick,
		trackVoucherinLayerClick,
		trackPromotionFormLayerLoad,
		trackPromotionSubmissionForm,
		trackVwBankLayerLoad,
		trackVwBankButtonClick,
		trackSalesforceVoucherDetailBackClick,
	};
};
